/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentHero.module.scss';

export const GovernmentHero = () => {
  const { t } = useInt();
  const civicRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(civicRef, { threshold: 0.2 });

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Verify any company against the register that issued it.')}
            description={
              <>
                {t('Global Database gives public bodies corporate records read directly from')}{' '}
                <b>{t('400+ government registers across 200+ countries')}</b>.{' '}
                {t(
                  'Ownership, control, officers and status — each field carries the register it came from, the filing it was read out of, and the date it was retrieved.',
                )}
              </>
            }
          />
          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/book-a-demo">
              {t('Book a briefing')}
            </Button>
            <Button type="dark-outline" size="xxl" href="/data-sources">
              {t('Check data sources')}
            </Button>
          </div>
        </div>

        <div className={cn(styles.civic, revealed && styles.civicOn)} ref={civicRef}>
          <svg
            className={styles.civicArt}
            viewBox="0 0 620 480"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Line elevation of a classical government register building"
          >
            <g fill="none" strokeWidth="1.1" strokeLinejoin="round">
              <path d="M272 62 A 40 38 0 0 1 348 62" className={styles.fillMist} />
              <path d="M310 24 v-14" strokeWidth="1.4" />
              <rect x="301" y="24" width="18" height="16" className={styles.fillPaper} />
              <rect x="272" y="62" width="76" height="44" className={styles.fillPaper} />
              <path d="M286 62 v44 M310 62 v44 M334 62 v44" strokeOpacity="0.38" />

              <path d="M92 176 L310 104 L528 176 Z" className={styles.fillPaper} />
              <path d="M120 168 L310 105.5 L500 168" strokeOpacity="0.35" />
              <circle cx="310" cy="150" r="9" className={styles.dot} strokeWidth="1.3" />

              <rect x="92" y="176" width="436" height="12" />
              <rect x="100" y="188" width="420" height="24" className={styles.fillMist} />
              <path d="M100 200 H520" strokeOpacity="0.3" />

              <path d="M287 396 v-96 a23 23 0 0 1 46 0 v96" className={styles.fillMist} strokeOpacity="0.45" />

              <g className={styles.fillPaper}>
                <rect x="116" y="212" width="30" height="12" />
                <rect x="120" y="224" width="22" height="164" />
                <rect x="115" y="388" width="32" height="8" />
                <rect x="167" y="212" width="30" height="12" />
                <rect x="171" y="224" width="22" height="164" />
                <rect x="166" y="388" width="32" height="8" />
                <rect x="218" y="212" width="30" height="12" />
                <rect x="222" y="224" width="22" height="164" />
                <rect x="217" y="388" width="32" height="8" />
                <rect x="269" y="212" width="30" height="12" />
                <rect x="273" y="224" width="22" height="164" />
                <rect x="268" y="388" width="32" height="8" />
                <rect x="320" y="212" width="30" height="12" />
                <rect x="324" y="224" width="22" height="164" />
                <rect x="319" y="388" width="32" height="8" />
                <rect x="371" y="212" width="30" height="12" />
                <rect x="375" y="224" width="22" height="164" />
                <rect x="370" y="388" width="32" height="8" />
                <rect x="422" y="212" width="30" height="12" />
                <rect x="426" y="224" width="22" height="164" />
                <rect x="421" y="388" width="32" height="8" />
                <rect x="473" y="212" width="30" height="12" />
                <rect x="477" y="224" width="22" height="164" />
                <rect x="472" y="388" width="32" height="8" />
              </g>
              <g strokeOpacity="0.26" strokeWidth="0.9">
                <path d="M127 230 v152 M134 230 v152 M178 230 v152 M185 230 v152 M229 230 v152 M236 230 v152 M280 230 v152 M287 230 v152 M331 230 v152 M338 230 v152 M382 230 v152 M389 230 v152 M433 230 v152 M440 230 v152 M484 230 v152 M491 230 v152" />
              </g>

              <g className={styles.fillPaper}>
                <rect x="100" y="396" width="420" height="12" />
                <rect x="85" y="408" width="450" height="12" />
                <rect x="70" y="420" width="480" height="12" />
              </g>
              <path d="M24 432 H596" strokeWidth="1.4" />
            </g>
          </svg>
          <div className={styles.civicCap}>
            <span className={styles.civicCapL}>{t('One jurisdiction · one register')}</span>
            <span className={styles.civicCapR}>{t('400+ more begin past the steps ↓')}</span>
          </div>
        </div>
      </div>
    </Section>
  );
};
